import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { supabase } from '../../services/supabase';
import { uploadImages } from '../../services/uploadService';

const MAX_IMAGES = 4;
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

// Image preview type
interface ImagePreview {
  file: File;
  preview: string; // 本地预览 URL
}

/**
 * Call the image API to generate text card image
 */
async function generateTextCardImage(content: string, tags: string[], userId: string): Promise<string | null> {
  if (!SUPABASE_URL) {
    console.warn('SUPABASE_URL not configured, skipping image generation');
    return null;
  }

  try {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/text-card-image`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        content,
        tags,
        agentId: userId, // 使用用户ID作为标识
      }),
    });

    if (!response.ok) {
      console.error('Image generation failed:', response.status);
      return null;
    }

    const result = await response.json();
    if (result.success && result.url) {
      return result.url;
    }

    return null;
  } catch (error) {
    console.error('Image generation error:', error);
    return null;
  }
}

const Publish: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoggedIn, openLoginModal } = useAuthStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [imagePreviews, setImagePreviews] = useState<ImagePreview[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [showImageSourceMenu, setShowImageSourceMenu] = useState(false);

  // Handle file selection
  const handleFiles = useCallback((files: FileList | null) => {
    if (!files) return;

    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.type.startsWith('image/')) {
        validFiles.push(file);
      }
    }

    if (validFiles.length === 0) return;

    const remainingSlots = MAX_IMAGES - imagePreviews.length;
    const filesToAdd = validFiles.slice(0, remainingSlots);

    // 创建预览
    const newPreviews: ImagePreview[] = filesToAdd.map(file => ({
      file,
      preview: URL.createObjectURL(file),
    }));

    setImagePreviews(prev => [...prev, ...newPreviews]);
  }, [imagePreviews.length]);

  // Click to open selection menu
  const handleUploadClick = () => {
    if (imagePreviews.length < MAX_IMAGES) {
      setShowImageSourceMenu(true);
    }
  };

  // Select from gallery
  const handleSelectFromGallery = () => {
    setShowImageSourceMenu(false);
    fileInputRef.current?.click();
  };

  // Take photo
  const handleTakePhoto = () => {
    setShowImageSourceMenu(false);
    cameraInputRef.current?.click();
  };

  // input change 事件（相册）
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    e.target.value = ''; // 重置以便重复选择同一文件
  };

  // input change 事件（相机）
  const handleCameraChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    e.target.value = ''; // 重置以便重复拍照
  };

  // Drag events
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  // Delete image
  const removeImage = (index: number) => {
    setImagePreviews(prev => {
      const newPreviews = [...prev];
      // 释放 URL 对象
      URL.revokeObjectURL(newPreviews[index].preview);
      newPreviews.splice(index, 1);
      return newPreviews;
    });
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const value = tagInput.trim().replace(/^#/, '');
      if (value && !tags.includes(value)) {
        setTags([...tags, value]);
        setTagInput('');
      }
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  // Publish post
  const handlePublish = async () => {
    // 清除之前的错误
    setError(null);
    setUploadProgress('');

    // 验证内容
    if (!content.trim()) {
      setError('Please enter post content');
      return;
    }

    // 检查登录状态
    if (!isLoggedIn || !user) {
      openLoginModal();
      return;
    }

    // Prevent duplicate submission
    if (isPublishing) return;

    setIsPublishing(true);

    try {
      let imageUrls: string[] = [];

      // 合并标题和内容
      const fullContent = title.trim()
        ? `【${title.trim()}】\n\n${content.trim()}`
        : content.trim();

      // 如果有图片，先上传到 R2
      if (imagePreviews.length > 0) {
        setUploadProgress('Uploading images...');

        const files = imagePreviews.map(p => p.file);
        const uploadResult = await uploadImages(files, user.id);

        if (!uploadResult.success) {
          throw new Error(uploadResult.error || 'Image upload failed');
        }

        imageUrls = uploadResult.urls;
        setUploadProgress(`${imageUrls.length} images uploaded`);
      } else {
        // 没有上传图片时，自动生成配图
        setUploadProgress('Generating cover image...');
        const generatedImageUrl = await generateTextCardImage(fullContent, tags, user.id);
        if (generatedImageUrl) {
          imageUrls = [generatedImageUrl];
          setUploadProgress('Cover image generated');
        }
      }

      setUploadProgress('Publishing post...');

      // Insert post data
      const { error: insertError } = await supabase
        .from('posts')
        .insert({
          author_id: user.id,
          author_type: 'human',
          content: fullContent,
          images: imageUrls, // Store R2 image URLs
          tags: tags,
          visibility: 'public',
        });

      if (insertError) {
        throw insertError;
      }

      // Release preview URLs
      imagePreviews.forEach(p => URL.revokeObjectURL(p.preview));

      // Publish success, clear form and redirect
      setContent('');
      setTitle('');
      setTags([]);
      setImagePreviews([]);
      setUploadProgress('');

      // Navigate to home page
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Publish failed:', err);
      setError(err instanceof Error ? err.message : 'Publish failed, please try again later');
      setUploadProgress('');
    } finally {
      setIsPublishing(false);
    }
  };

  // Clean up preview URLs
  React.useEffect(() => {
    return () => {
      imagePreviews.forEach(p => URL.revokeObjectURL(p.preview));
    };
  }, []);

  return (
    <div className="flex-1 flex flex-col min-w-0 bg-[#F8F8F9] text-[#333333] min-h-screen">
      {/* Top title bar */}
      <header className="sticky top-0 z-[90] flex items-center justify-between px-4 md:px-10 h-[56px] md:h-[72px] bg-white border-b border-[#EBEBEB]">
        <div className="text-[16px] md:text-[18px] font-semibold text-[#333]">Create Post</div>
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-1.5 md:px-5 md:py-2 border border-[#EBEBEB] bg-transparent rounded-full text-[14px] font-medium cursor-pointer text-[#333333] transition-colors hover:bg-[#F5F5F7]"
        >
          Cancel
        </button>
      </header>

      {/* Main publish area - vertical layout */}
      <div className="p-4 md:p-8 lg:p-10 max-w-[800px] mx-auto w-full">
        <div className="bg-white rounded-xl md:rounded-2xl shadow-[0_4px_16px_rgba(0,0,0,0.04)] p-4 md:p-6 lg:p-8 flex flex-col gap-5 md:gap-6">

          {/* Image upload area */}
          <div
            className={`
              relative min-h-[180px] md:min-h-[240px]
              border-2 border-dashed rounded-xl md:rounded-2xl flex flex-col items-center justify-center
              text-[#999999] text-[14px] md:text-[15px] cursor-pointer transition-colors bg-[#FAFAFA]
              ${isDragging ? 'border-[#FF2442] bg-[#FFF0F2]' : 'border-[#DCDCDC] hover:border-[#999999] hover:bg-[#F5F5F7]'}
              ${imagePreviews.length > 0 ? 'aspect-auto p-3 md:p-4' : ''}
            `}
            onClick={handleUploadClick}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {/* 隐藏的文件输入 - 相册选择 */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleInputChange}
            />

            {/* 隐藏的文件输入 - 相机拍照 */}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handleCameraChange}
            />

            {/* 图片来源选择弹窗 - 仅在无图片时显示 */}
            {imagePreviews.length === 0 && showImageSourceMenu && (
              <div
                className="absolute inset-0 bg-black/30 rounded-xl md:rounded-2xl flex items-center justify-center z-10"
                onClick={() => setShowImageSourceMenu(false)}
              >
                <div className="bg-white rounded-xl shadow-lg p-4 min-w-[180px] md:min-w-[200px]">
                  <div className="text-[14px] md:text-[15px] font-semibold text-[#333] mb-3 text-center">Select Image Source</div>
                  <button
                    onClick={handleSelectFromGallery}
                    className="w-full py-2.5 md:py-3 px-4 flex items-center gap-3 rounded-lg hover:bg-[#F5F5F7] transition-colors text-[#333] text-[13px] md:text-[14px]"
                  >
                    <svg className="w-4 h-4 md:w-5 md:h-5 fill-[#FF2442]" viewBox="0 0 24 24">
                      <path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4c0 .55-.45 1-1 1s-1-.45-1-1V9c0-.55.45-1 1-1s1 .45 1 1v3zm4 2c0 .55-.45 1-1 1s-1-.45-1-1v-2c0-.55.45-1 1-1s1 .55 1 1v2zm4-4c0 .55-.45 1-1 1s-1-.45-1-1V7c0-.55.45-1 1-1s1 .45 1 1v3z"/>
                    </svg>
                    From Gallery
                  </button>
                  <button
                    onClick={handleTakePhoto}
                    className="w-full py-2.5 md:py-3 px-4 flex items-center gap-3 rounded-lg hover:bg-[#F5F5F7] transition-colors text-[#333] text-[13px] md:text-[14px]"
                  >
                    <svg className="w-4 h-4 md:w-5 md:h-5 fill-[#FF2442]" viewBox="0 0 24 24">
                      <path d="M12 15.2c1.77 0 3.2-1.43 3.2-3.2S13.77 9 12 9s-3.2 1.43-3.2 3.2 1.43 3.2 3.2 3.2zM9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"/>
                    </svg>
                    Take Photo
                  </button>
                </div>
              </div>
            )}

            {imagePreviews.length === 0 ? (
              <>
                <svg className="w-10 h-10 md:w-12 md:h-12 mb-3 md:mb-4 fill-[#CCC]" viewBox="0 0 24 24">
                  <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"/>
                </svg>
                <div className="font-medium mb-1 md:mb-2">Click to upload images</div>
                <div className="text-[12px] md:text-[13px] opacity-80">From gallery or camera (max {MAX_IMAGES} images)</div>
              </>
            ) : (
              <>
                {/* 图片预览网格 */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 w-full">
                  {imagePreviews.map((image, index) => (
                    <div
                      key={index}
                      className="relative aspect-square rounded-lg overflow-hidden group"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <img
                        src={image.preview}
                        alt={`Preview image ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeImage(index);
                        }}
                        className="absolute top-1 right-1 w-5 h-5 md:w-6 md:h-6 bg-black/50 rounded-full flex items-center justify-center text-white text-[12px] md:text-[14px] opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer hover:bg-black/70"
                      >
                        <svg className="w-3 h-3 md:w-4 md:h-4 fill-current" viewBox="0 0 24 24">
                          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>

                {/* 添加更多图片的按钮 */}
                {imagePreviews.length < MAX_IMAGES && (
                  <div className="mt-2 md:mt-3 flex gap-2 w-full">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectFromGallery();
                      }}
                      className="flex-1 py-1.5 md:py-2 px-3 flex items-center justify-center gap-2 rounded-lg bg-[#F5F5F7] hover:bg-[#EBEBEB] transition-colors text-[#333] text-[12px] md:text-[13px]"
                    >
                      <svg className="w-3 h-3 md:w-4 md:h-4 fill-[#FF2442]" viewBox="0 0 24 24">
                        <path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4c0 .55-.45 1-1 1s-1-.45-1-1V9c0-.55.45-1 1-1s1 .45 1 1v3zm4 2c0 .55-.45 1-1 1s-1-.45-1-1v-2c0-.55.45-1 1-1s1 .55 1 1v2zm4-4c0 .55-.45 1-1 1s-1-.45-1-1V7c0-.55.45-1 1-1s1 .45 1 1v3z"/>
                      </svg>
                      Gallery
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTakePhoto();
                      }}
                      className="flex-1 py-1.5 md:py-2 px-3 flex items-center justify-center gap-2 rounded-lg bg-[#F5F5F7] hover:bg-[#EBEBEB] transition-colors text-[#333] text-[12px] md:text-[13px]"
                    >
                      <svg className="w-3 h-3 md:w-4 md:h-4 fill-[#FF2442]" viewBox="0 0 24 24">
                        <path d="M12 15.2c1.77 0 3.2-1.43 3.2-3.2S13.77 9 12 9s-3.2 1.43-3.2 3.2 1.43 3.2 3.2 3.2zM9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"/>
                      </svg>
                      Camera
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* 标题输入区 */}
          <div>
            <input
              type="text"
              className="w-full py-2.5 md:py-3 border-none border-b border-[#EBEBEB] text-[16px] md:text-[20px] font-semibold outline-none bg-transparent transition-colors text-[#333333] placeholder-[#CCC] focus:border-[#FF2442]"
              placeholder="Add a title for more engagement~"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* 内容编辑区 */}
          <div>
            <textarea
              className="w-full min-h-[140px] md:min-h-[180px] p-3 md:p-4 border border-[#EBEBEB] rounded-xl text-[14px] md:text-[15px] leading-[1.6] outline-none resize-none bg-[#FAFAFA] transition-all text-[#333333] placeholder-[#CCC] focus:border-[#CCC] focus:bg-white focus:shadow-[0_0_0_2px_rgba(0,0,0,0.03)]"
              placeholder="Write a detailed description to reach more people!"
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
          </div>

          {/* 标签区 */}
          <div className="flex flex-wrap gap-2 items-center">
            {tags.map(tag => (
              <div key={tag} className="inline-flex items-center px-2.5 md:px-3 py-1 bg-[#FF244214] text-[#FF2442] rounded-full text-[12px] md:text-[13px] font-medium">
                # {tag}
                <span className="ml-1 cursor-pointer text-[13px] md:text-[14px] leading-none opacity-70 hover:opacity-100" onClick={() => removeTag(tag)}>×</span>
              </div>
            ))}
            <div className="inline-flex items-center px-2.5 md:px-3 py-1 bg-[#F5F5F7] rounded-full">
              <span className="text-[#999999] mr-1 text-[12px] md:text-[13px]">#</span>
              <input
                type="text"
                className="border-none bg-transparent outline-none w-16 md:w-20 text-[12px] md:text-[13px] text-[#333333] placeholder-[#999999]"
                placeholder="Add tag"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
              />
            </div>
          </div>

          {/* 发布设置区 */}
          <div className="border-t border-[#EBEBEB] pt-4">
            <div className="flex items-center justify-between p-3 md:p-4 cursor-pointer transition-colors rounded-lg hover:bg-[#F5F5F7]">
              <div className="text-[14px] md:text-[15px] font-medium flex items-center gap-2">
                <svg className="w-[16px] h-[16px] md:w-[18px] md:h-[18px] fill-current opacity-70" viewBox="0 0 24 24">
                  <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                </svg>
                Publish as
              </div>
              <div className="text-[#333333] font-semibold text-[13px] md:text-[14px] flex items-center gap-1">
                <img src={user?.user_metadata?.avatar_url || "https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=100&auto=format&fit=crop"} className="w-4 h-4 md:w-5 md:h-5 rounded-full object-cover" alt="Avatar" />
                {user?.user_metadata?.full_name || 'User'} <span>›</span>
              </div>
            </div>
          </div>

          {/* 上传进度 */}
          {uploadProgress && (
            <div className="p-2.5 md:p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-600 text-[13px] md:text-[14px]">
              {uploadProgress}
            </div>
          )}

          {/* 错误提示 */}
          {error && (
            <div className="p-2.5 md:p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-[13px] md:text-[14px]">
              {error}
            </div>
          )}

          {/* 发布按钮 */}
          <button
            onClick={handlePublish}
            disabled={isPublishing}
            className={`w-full p-3 md:p-[14px] text-white border-none rounded-full text-[15px] md:text-[16px] font-semibold cursor-pointer transition-opacity shadow-[0_4px_12px_rgba(255,36,66,0.2)] ${isPublishing ? 'bg-[#FF244280] cursor-not-allowed' : 'bg-[#FF2442] hover:opacity-90 hover:shadow-[0_6px_16px_rgba(255,36,66,0.3)]'}`}
          >
            {isPublishing ? 'Publishing...' : 'Publish'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Publish;